
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image';
import { getPostBySlug, getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service';
import { s3Static } from '../../../utils/constants';
import { BlogHeaderLinks } from '../../../components/blogs/BlogHeaderLinks';
import { getRequestJSON } from '../../../api/index/location-api';
import { getHandymanPriceList } from '../../../api/handyman/handyman-service-api';
import Link from 'next/link';
import { blogsMaster } from '../../../utils/sg-blogs-data';
import { BlogsList } from '../../../components/blogs/BlogList';
import { getWpNewHouseKeeping, setWpNewHouseKeeping } from '../../../service/wp-blogs-store';
import { getHandymanSlugData, setHandymanSlugData } from '../../../service/page-slug-store';
import { set } from 'lodash';
import { HandymanServiceRequestFormSlug } from '@/index/service-category/handyman/HandymanServiceRequestFormSlug';
import { STATIC_HANDYMAN_SLUGS } from '../../../data/handyman-job-pages';

export const getStaticPaths = async () => {
    const posts = await getWpNewPostsByCategory(100, "repair-renovation");
    const oldPosts = await getWpPostsByCategory(100, "repair-renovation");
    posts.push(...oldPosts)

    // Static pages (the price list and job pages) share this folder. If a
    // WordPress post ever uses one of their slugs, Next.js fails the build with
    // conflicting SSG paths, so drop the post and log it. The static page wins.
    const slugs = posts.map((post) => post.slug).filter((slug) => {
      if (STATIC_HANDYMAN_SLUGS.includes(slug)) {
        console.warn(`[handyman [slug]] skipping WordPress post "${slug}": a static page owns that URL`)
        return false
      }
      return true
    })

    return {
      paths: [...new Set(slugs)].map((slug) => `/sg/handyman-singapore-services/${slug}`),
      fallback: false,
    };
  };
  
export const getStaticProps = async ({ params }) => {
    const countryName = "Singapore"  
    const post = await getPostBySlug(params?.slug)

    const blogs = blogsMaster['repair-renovation'].blogArray
    
    const wpNewPosts = getWpNewHouseKeeping().length > 0 ?  getWpNewHouseKeeping() : await getWpNewPostsByCategory(6, "repair-renovation")
    setWpNewHouseKeeping(wpNewPosts)

    const handymanPriceList = getHandymanSlugData().length > 0 ?  getHandymanSlugData() : await getHandymanPriceList(countryName)
    setHandymanSlugData(handymanPriceList)

    const allRequestCategorySettings = await getRequestJSON(countryName)
    
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Handyman Services")

    return {
      props: { 
        post, 
        blogs,
        wpNewPosts,
        countryName, 
        requestCategorySettings,
        handymanPriceList
      },
      revalidate: 259200, // In seconds
    };
};

export default function Home(props) {
  const blogs = props.blogs
  const wpNewPosts = props.wpNewPosts

  const post = props.post
  const description = post.excerpt.replace(/<[^>]+>/g, '').trim()
  const countryName = props.countryName
  const requestCategorySettings = props.requestCategorySettings
  const handymanPriceList = props.handymanPriceList

  return (
    <>
      <Head>
        <title>{post.title}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={description}/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/handyman-singapore-services/${post.slug}`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={post.title}/>
        <meta property="og:description" content={description} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.modified} />
        <meta property="og:image"  content={post?.featuredImage?.node?.sourceUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={post?.featuredImage?.node?.sourceUrl} />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/handyman-singapore-services/${post.slug}`}/>
        <meta name="twitter:title" content={post.title}/> 
        <meta name="twitter:description" content={description}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={`https://www.stringssg.com/sg/handyman-singapore-services/${post.slug}`} />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"${`https://www.stringssg.com/sg/handyman-singapore-services/${post.slug}`}"},"headline":"${post.title}","description": "${description}","image":["${post?.featuredImage?.node?.sourceUrl}"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"${post.date}","dateModified":"${post.modified}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"handyman-sg","item":"https://www.stringssg.com/sg/handyman-singapore-services"},{"@type":"ListItem","position":2,"name":"${post.slug}"}]}]}`
          }}
        />
      </Head>
      <div>
        <HandymanServiceRequestFormSlug countryName={countryName} requestCategorySettings={requestCategorySettings} handymanPriceList={handymanPriceList} post={post}/>
        <section className="py-10 space-y-10">
            <div className='max-w-5xl mx-auto px-2 space-y-10'>
              <h1 className="text-2xl md:text-3xl text-center font-bold">{post.title}</h1>
              <div
                  className="post-content w-full px-2 mx-auto space-y-5 leading-8"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  ></div>
            </div>
            <div className='text-center relative'>
                <div className='relative w-full h-96'>
                    <Image
                        src={s3Static + "handyman/handyman-singapore.jpg"}
                        fill
                        style={{objectFit: 'cover', objectPosition: 'center'}}   
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='Handyman Service - StringsSG'
                    />     
                </div> 
                <div className='absolute flex items-center text-white top-0 bottom-0 left-0 right-0 w-fit mx-auto'>
                    <Link href="/sg/handyman-singapore-services" prefetch={false} className='py-4 px-8 rounded-2xl bg-black/60 text-xl md:text-2xl'>
                          Book Handyman/Plumber
                    </Link>       
                </div>  
            </div>
        </section>
        <BlogsList blogs={blogs} wpNewPosts={wpNewPosts}/>
        <div className='bg-gray-800 text-white py-4 p-2 text-center text-2xl'>
          Topics
        </div>
        <BlogHeaderLinks/>
      </div>
      <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}